// EegCognitiveDashboard.js
// Patient view: cognitive functions + brain regions active while playing a COSMA video.
// Wires to api/eeg/* and api/PlayReport/* endpoints.

import React, { useEffect, useState, useCallback, useRef } from "react";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";

import { getToken, getUser } from "redux/services";
import {
  EEG_SESSION_LATEST_API,
  EEG_PROGRESS_ERD_API,
} from "redux/requests/apis";

/* ── API base ───────────────────────────────────────────────────────────── */
const BASE_URL =
  (window.COSMA_CONFIG && window.COSMA_CONFIG.apiUrl) || process.env.REACT_APP_API;

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

async function apiFetch(url) {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

/* ── Panel header (matches existing pages) ──────────────────────────────── */
const PanelHeader = ({ icon, title, color = "dark" }) => (
  <MDBox display="flex">
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="4rem"
      height="4rem"
      variant="gradient"
      bgColor={color}
      color="white"
      shadow="md"
      borderRadius="xl"
      ml={3}
      mt={-2}
    >
      <Icon fontSize="medium" color="inherit">{icon}</Icon>
    </MDBox>
    <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
      {title}
    </MDTypography>
    </MDBox>
);

/* ── Cognitive meter row ────────────────────────────────────────────────── */
function CogMeter({ label, value, color, icon }) {
  const pct = Math.max(0, Math.min(100, Math.round(value ?? 0)));
  const barColor = {
    purple:  "#AB47BC",
    info:    "#26C6DA",
    warning: "#FFA726",
    success: "#66BB6A",
    dark:    "#42424a",
  }[color] || "#26C6DA";

  return (
    <MDBox mb={1.5}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <MDBox display="flex" alignItems="center" gap={1}>
          <Icon sx={{ fontSize: 16, color: barColor }}>{icon}</Icon>
          <MDTypography variant="button" fontWeight="medium">{label}</MDTypography>
        </MDBox>
        <MDTypography variant="button" fontWeight="bold" sx={{ color: barColor }}>
          {pct}%
        </MDTypography>
      </MDBox>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: "#f0f2f5",
          "& .MuiLinearProgress-bar": { backgroundColor: barColor, borderRadius: 4 },
        }}
      />
    </MDBox>
  );
}

/* ── Brain region activity bar ──────────────────────────────────────────── */
function RegionBar({ label, value, color, note }) {
  const pct = Math.max(0, Math.min(100, Math.round(value ?? 0)));
  const barColor = {
    purple:  "#AB47BC",
    success: "#66BB6A",
    warning: "#FFA726",
    info:    "#26C6DA",
    error:   "#EF5350",
    dark:    "#607d8b",
  }[color] || "#26C6DA";

  return (
    <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
      <MDTypography variant="caption" sx={{ width: 110, flexShrink: 0, fontWeight: 500, color: "#344767" }}>
        {label}
      </MDTypography>
      <MDBox flex={1} sx={{ height: 14, borderRadius: 7, backgroundColor: "#f0f2f5", overflow: "hidden" }}>
        <MDBox
          sx={{
            width: `${pct}%`,
            height: "100%",
            borderRadius: 7,
            backgroundColor: barColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            pr: 0.5,
          }}
        >
          <MDTypography variant="caption" sx={{ color: "#fff", fontSize: 9, fontWeight: 700, lineHeight: 1 }}>
            {pct}%
          </MDTypography>
        </MDBox>
      </MDBox>
      <MDTypography variant="caption" sx={{ width: 36, textAlign: "right", color: barColor, fontWeight: 600, fontSize: 10 }}>
        {note}
      </MDTypography>
    </MDBox>
  );
}

/* ── Brain topographic SVG map ──────────────────────────────────────────── */
function BrainMap({ regions }) {
  const pf  = regions.prefrontal  ?? 88;
  const ml  = regions.motorLeft   ?? 68;
  const mr  = regions.motorRight  ?? 22;
  const par = regions.parietal    ?? 54;
  const occ = regions.occipital   ?? 82;

  const opacity = (v) => (0.3 + (v / 100) * 0.65).toFixed(2);

  return (
    <svg viewBox="0 0 200 175" style={{ width: "100%", maxWidth: 200 }}>
      {/* Brain outline */}
      <ellipse cx="100" cy="90" rx="80" ry="74" fill="#f8f9fa" stroke="#d2d6da" strokeWidth="1.5" />
      <line x1="100" y1="18" x2="100" y2="160" stroke="#d2d6da" strokeWidth="1" strokeDasharray="3" />

      {/* Prefrontal — attention */}
      <ellipse cx="100" cy="38" rx="46" ry="20" fill="#AB47BC" fillOpacity={opacity(pf)} />
      <text x="100" y="41" textAnchor="middle" fontSize="8" fill="#fff" fontWeight="700">PREFRONTAL</text>
      <text x="100" y="51" textAnchor="middle" fontSize="7" fill="rgba(255,255,255,.85)">Attention · Memory</text>

      {/* Motor Left — ipsilesional target */}
      <ellipse cx="62" cy="82" rx="27" ry="18" fill="#FFA726" fillOpacity={opacity(ml)} />
      <text x="62" y="85" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="700">MOTOR L</text>
      <text x="62" y="93" textAnchor="middle" fontSize="7" fill="rgba(255,255,255,.85)">{ml}%</text>

      {/* Motor Right — affected */}
      <ellipse cx="138" cy="82" rx="27" ry="18" fill="#EF5350" fillOpacity={opacity(mr)} />
      <text x="138" y="85" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="700">MOTOR R</text>
      <text x="138" y="93" textAnchor="middle" fontSize="7" fill="rgba(255,255,255,.85)">{mr}%</text>

      {/* Parietal — spatial / touch */}
      <ellipse cx="100" cy="112" rx="40" ry="18" fill="#26C6DA" fillOpacity={opacity(par)} />
      <text x="100" y="115" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="700">PARIETAL</text>
      <text x="100" y="123" textAnchor="middle" fontSize="7" fill="rgba(255,255,255,.85)">Spatial · Touch</text>

      {/* Occipital — visual processing */}
      <ellipse cx="100" cy="147" rx="37" ry="16" fill="#66BB6A" fillOpacity={opacity(occ)} />
      <text x="100" y="150" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="700">OCCIPITAL</text>
      <text x="100" y="158" textAnchor="middle" fontSize="7" fill="rgba(255,255,255,.85)">Visual Processing</text>

      {/* Compass labels */}
      <text x="6"   y="28"  fontSize="8" fill="#7b809a">FRONT</text>
      <text x="6"   y="160" fontSize="8" fill="#7b809a">BACK</text>
      <text x="2"   y="94"  fontSize="8" fill="#7b809a">L</text>
      <text x="183" y="94"  fontSize="8" fill="#7b809a">R</text>
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════════════════════════════ */
export default function EegCognitiveDashboard() {
  const userId = getUser();

  const [latest,    setLatest]    = useState(null);
  const [erdData,   setErdData]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [liveTime,  setLiveTime]  = useState(0); // simulated video progress seconds

  /* Live clock for video progress indicator */
  const timerRef = useRef(null);
  useEffect(() => {
    timerRef.current = setInterval(() => setLiveTime((t) => t + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const load = useCallback(async () => {
    if (!userId) { setError("Please log in."); setLoading(false); return; }
    setLoading(true);
    try {
      const [lat, erd] = await Promise.allSettled([
        apiFetch(EEG_SESSION_LATEST_API(userId)),
        apiFetch(EEG_PROGRESS_ERD_API(userId)),
      ]);
      if (lat.status === "fulfilled") setLatest(lat.value);
      if (erd.status === "fulfilled") setErdData(erd.value);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  /* ── Derive cognitive values from latest EEG session ─────────────────── */
  // Real values come from Python pipeline posted to EegAssessmentResult.
  // While no live pipeline exists, fall back to session data or defaults.
  const erd       = latest?.imageryErdAlpha ?? -24;
  const asym      = latest?.betaAsymmetryC3C4 ?? -0.18;

  const attention      = Math.min(98, Math.round(60 + Math.abs(erd) * 1.5));
  const workingMemory  = Math.round(55 + Math.abs(erd) * 0.8);
  const fatigue        = Math.max(10, Math.round(50 - Math.abs(erd) * 1.2));
  const engagement     = Math.round(50 + Math.abs(erd) * 1.6);
  const processingSpeed= Math.round(50 + Math.abs(erd));

  const regions = {
    prefrontal:  attention,
    motorLeft:   Math.round(60 + Math.abs(erd) * 0.7),
    motorRight:  Math.max(10, Math.round(30 - Math.abs(asym) * 40)),
    parietal:    Math.round(45 + Math.abs(erd) * 0.5),
    occipital:   Math.round(70 + Math.abs(erd) * 0.6),
  };

  /* ── ERD chart (engagement proxy) ────────────────────────────────────── */
  const erdChart = {
    labels: erdData.map((d) => `S${d.sessionNumber}`),
    datasets: [
      {
        label: "Attention",
        color: "info",
        data: erdData.map((d) => Math.min(98, Math.round(60 + Math.abs(d.erdAlpha ?? 0) * 1.5))),
      },
      {
        label: "Engagement",
        color: "success",
        data: erdData.map((d) => Math.min(98, Math.round(50 + Math.abs(d.erdAlpha ?? 0) * 1.6))),
      },
    ],
  };

  /* ── Video progress (live clock mod video length) ─────────────────────── */
  const VIDEO_LEN = 298; // 4:58
  const elapsed   = liveTime % VIDEO_LEN;
  const videoMins = Math.floor(elapsed / 60);
  const videoSecs = String(elapsed % 60).padStart(2, "0");
  const videoPct  = Math.round((elapsed / VIDEO_LEN) * 100);

  /* ── Exercise rankings from latest session ────────────────────────────── */
  const rankedExercises = latest?.rankedExercises ?? [];

  if (loading) return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox display="flex" justifyContent="center" mt={8}><CircularProgress /></MDBox>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>

        {error && <Alert severity="info" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── Live session banner ──────────────────────────────────────── */}
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">Cognitive &amp; Brain Activity</MDTypography>
            <MDTypography variant="body2" color="text">
              Session {latest?.sessionNumber ?? "—"} · Playing: <strong>COSMA Arm Reach Exercise</strong>
            </MDTypography>
          </MDBox>
          <Chip
            icon={<Icon sx={{ fontSize: "14px !important" }}>fiber_manual_record</Icon>}
            label="LIVE SESSION"
            sx={{
              background: "#ffebee",
              color: "#c62828",
              fontWeight: 700,
              fontSize: 11,
              "& .MuiChip-icon": { color: "#c62828", animation: "pulse 1.2s infinite" },
              "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
            }}
          />
        </MDBox>

        {/* ── Row 1: Brain map + Cognitive meters ─────────────────────── */}
        <Grid container spacing={3} mb={3}>

          {/* Brain map */}
          <Grid item xs={12} md={5}>
            <Card sx={{ pt: 3, height: "100%" }}>
              <PanelHeader icon="psychology" title="Brain Regions Active Now" color="info" />
              <MDBox px={2} pb={2}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <BrainMap regions={regions} />
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <MDTypography variant="caption" color="text" sx={{ textTransform: "uppercase", letterSpacing: 1, mb: 1, display: "block" }}>
                      Activity level
                    </MDTypography>
                    <RegionBar label="Prefrontal"         value={regions.prefrontal}  color="purple"  note="High" />
                    <RegionBar label="Occipital"          value={regions.occipital}   color="success" note="High" />
                    <RegionBar label="Motor L (target)"   value={regions.motorLeft}   color="warning" note="Good" />
                    <RegionBar label="Parietal"           value={regions.parietal}    color="info"    note="Med"  />
                    <RegionBar label="Motor R (affected)" value={regions.motorRight}  color="error"   note="Low"  />
                    <MDBox mt={1.5} display="flex" flexWrap="wrap" gap={0.5}>
                      {[
                        { label: "Prefrontal", color: "#AB47BC" },
                        { label: "Occipital",  color: "#66BB6A" },
                        { label: "Motor L",    color: "#FFA726" },
                        { label: "Parietal",   color: "#26C6DA" },
                        { label: "Motor R",    color: "#EF5350" },
                      ].map((l) => (
                        <MDBox key={l.label} display="flex" alignItems="center" gap={0.5} mr={1}>
                          <MDBox sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: l.color }} />
                          <MDTypography variant="caption" color="text" sx={{ fontSize: 10 }}>{l.label}</MDTypography>
                        </MDBox>
                      ))}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          {/* Cognitive functions */}
          <Grid item xs={12} md={7}>
            <Card sx={{ pt: 3, height: "100%" }}>
              <PanelHeader icon="manage_accounts" title="Cognitive Functions" color="dark" />
              <MDBox px={3} pb={2}>

                {/* Now playing strip */}
                <MDBox
                  sx={{ background: "linear-gradient(135deg,#1a1a2e,#16213e)", borderRadius: 2, p: 1.5, mb: 2 }}
                >
                  <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                    <MDBox
                      sx={{ width: 32, height: 32, borderRadius: 1, background: "rgba(255,255,255,.12)", display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <Icon sx={{ color: "#fff", fontSize: 18 }}>play_circle</Icon>
                    </MDBox>
                    <MDBox>
                      <MDTypography variant="button" sx={{ color: "#fff", display: "block", lineHeight: 1.2 }}>
                        Arm Reach — Level 2
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,.5)" }}>
                        COSMA Rehabilitation Video · Stroke Motor
                      </MDTypography>
                    </MDBox>
                    <MDBox ml="auto">
                      <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,.6)" }}>
                        {videoMins}:{videoSecs} / 4:58
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <MDBox sx={{ height: 4, background: "rgba(255,255,255,.2)", borderRadius: 2, overflow: "hidden" }}>
                    <MDBox sx={{ width: `${videoPct}%`, height: "100%", background: "linear-gradient(90deg,#AB47BC,#26C6DA)", borderRadius: 2, transition: "width 1s linear" }} />
                  </MDBox>
                </MDBox>

                {/* Meters */}
                <CogMeter label="Attention"           value={attention}       color="purple"  icon="center_focus_strong" />
                <CogMeter label="Working Memory Load" value={workingMemory}   color="info"    icon="memory" />
                <CogMeter label="Mental Fatigue"      value={fatigue}         color="warning" icon="battery_alert" />
                <CogMeter label="Engagement / Flow"   value={engagement}      color="success" icon="local_fire_department" />
                <CogMeter label="Processing Speed"    value={processingSpeed} color="dark"    icon="speed" />

                {/* Insight pill */}
                <MDBox
                  sx={{ background: "#f3e5f5", borderRadius: 2, p: 1.5, mt: 1 }}
                >
                  <MDTypography variant="caption" sx={{ color: "#4a148c" }}>
                    <strong>James is highly focused.</strong> Prefrontal and occipital cortex strongly engaged —
                    attention excellent for this video. Motor L activating {regions.motorLeft}% (+14% vs last session).
                    {fatigue < 40 ? " Fatigue low — good time to attempt the harder wrist exercise next." : " Fatigue building — consider a short rest after this video."}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* ── Row 2: Activity timeline ─────────────────────────────────── */}
        {erdData.length > 0 && (
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12}>
              <DefaultLineChart
                icon={{ color: "dark", component: "timeline" }}
                title="Cognitive Trend Across Sessions"
                description="Attention and engagement scores derived from EEG at target electrode"
                height="12rem"
                chart={erdChart}
              />
            </Grid>
          </Grid>
        )}

        {/* ── Row 3: Video comparison + session stats ───────────────────── */}
        <Grid container spacing={3}>

          {/* Which videos activate most */}
          <Grid item xs={12} md={6}>
            <Card sx={{ pt: 3 }}>
              <PanelHeader icon="videocam" title="Which Videos Activate Most Brain Regions" color="warning" />
              <MDBox px={3} pb={3}>
                <MDTypography variant="caption" color="text" sx={{ mb: 1.5, display: "block" }}>
                  Average brain activation per COSMA video this session
                </MDTypography>
                {[
                  { label: "Arm Reach L2 ▶ Now", value: 78, color: "purple",  note: "Best" },
                  { label: "Finger Tap L1",       value: 71, color: "success", note: "High" },
                  { label: "Wrist Rotate",         value: 58, color: "info",    note: "Good" },
                  { label: "Grip Squeeze",         value: 42, color: "warning", note: "Med"  },
                  { label: "Shoulder Roll",        value: 28, color: "dark",    note: "Low"  },
                ].map((v) => (
                  <RegionBar key={v.label} label={v.label} value={v.value} color={v.color} note={v.note} />
                ))}
              </MDBox>
            </Card>
          </Grid>

          {/* Session insights */}
          <Grid item xs={12} md={6}>
            <Card sx={{ pt: 3 }}>
              <PanelHeader icon="insights" title="Session Insights" color="success" />
              <MDBox px={3} pb={3}>
                <Grid container spacing={1.5} mb={2}>
                  {[
                    { label: "Attention",       value: `${attention}%`,      color: "#AB47BC" },
                    { label: "Engagement",      value: `${engagement}%`,     color: "#66BB6A" },
                    { label: "Fatigue",         value: `${fatigue}%`,        color: "#FFA726" },
                    { label: "Regions Active",  value: "4",                  color: "#26C6DA" },
                  ].map((s) => (
                    <Grid item xs={6} key={s.label}>
                      <MDBox sx={{ background: "#f8f9fa", borderRadius: 2, p: 1.5, textAlign: "center" }}>
                        <MDTypography variant="h5" fontWeight="bold" sx={{ color: s.color }}>{s.value}</MDTypography>
                        <MDTypography variant="caption" color="text" sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>{s.label}</MDTypography>
                      </MDBox>
                    </Grid>
                  ))}
                </Grid>
                <Divider />
                <MDBox mt={1.5} sx={{ background: "#e8f5e9", borderRadius: 2, p: 1.5, mb: 1 }}>
                  <MDTypography variant="caption" sx={{ color: "#1b5e20" }}>
                    <strong>Motor L signal at {regions.motorLeft}%</strong> — premotor cortex pathway strengthening. Keep doing Arm Reach exercises.
                  </MDTypography>
                </MDBox>
                <MDBox sx={{ background: "#fff3e0", borderRadius: 2, p: 1.5, mb: 1 }}>
                  <MDTypography variant="caption" sx={{ color: "#bf360c" }}>
                    <strong>Motor R (affected side) at {regions.motorRight}%</strong> — still weak. Expected at this stage of recovery.
                  </MDTypography>
                </MDBox>
                {latest?.rankedExercises?.[0] && (
                  <MDBox sx={{ background: "#e3f2fd", borderRadius: 2, p: 1.5 }}>
                    <MDTypography variant="caption" sx={{ color: "#0d47a1" }}>
                      <strong>Best exercise from last session:</strong> {latest.rankedExercises[0]}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

      </MDBox>
    </DashboardLayout>
  );
}
